const defectForm = document.querySelector('.defect-form');

if (defectForm) {

    // ── TEXTS ──

    const texts = {
        limitReached: defectForm.dataset.jsLimitReached,
        cannotAddMore: defectForm.dataset.jsCannotAddMore,
        maximumReached: defectForm.dataset.jsMaximumReached,
        addProductText: defectForm.dataset.jsAddProductText,
        minOneProduct: defectForm.dataset.jsMinOneProduct,
        sendingText: defectForm.dataset.jsSendingText,
        successMessage: defectForm.dataset.jsSuccessMessage,
        errorMessage: defectForm.dataset.jsErrorMessage,
        closeConfirmTitle: defectForm.dataset.jsCloseConfirmTitle,
        closeConfirmMessage: defectForm.dataset.jsCloseConfirmMessage,
        closeConfirmYes: defectForm.dataset.jsCloseConfirmYes,
        closeConfirmNo: defectForm.dataset.jsCloseConfirmNo,
        searchPlaceholder: defectForm.dataset.jsSearchPlaceholder,
        remove: defectForm.dataset.jsRemove,
        edit: defectForm.dataset.jsEdit,
        ok: defectForm.dataset.jsOk,
        deleteConfirmTitle: defectForm.dataset.jsDeleteConfirmTitle,
        deleteConfirmMessage: defectForm.dataset.jsDeleteConfirmMessage,
        deleteConfirmYes: defectForm.dataset.jsDeleteConfirmYes,
        deleteConfirmNo: defectForm.dataset.jsDeleteConfirmNo,
        editProductTitle: defectForm.dataset.jsEditProductTitle,
        imageDeleteConfirmTitle: defectForm.dataset.jsImageDeleteConfirmTitle,
        imageDeleteConfirmMessage: defectForm.dataset.jsImageDeleteConfirmMessage,
        imageDeleteConfirmYes: defectForm.dataset.jsImageDeleteConfirmYes,
        imageDeleteConfirmNo: defectForm.dataset.jsImageDeleteConfirmNo,
        minOneDefect: defectForm.dataset.jsMinOneDefect,
        maxImagesReached: defectForm.dataset.jsMaxImagesReached,
        imageTooLarge: defectForm.dataset.jsImageTooLarge,
        imageWrongType: defectForm.dataset.jsImageWrongType,
        imageRejectedTitle: defectForm.dataset.jsImageRejectedTitle,
        minOneImage: defectForm.dataset.jsMinOneImage,
        noProductSelected: defectForm.dataset.jsNoProductSelected,
        invalidTitle: defectForm.dataset.jsInvalidTitle,
        invoiceYearError: defectForm.dataset.jsInvoiceYearError,
        catalogUnavailable: defectForm.dataset.jsCatalogUnavailable,
        photosCount: defectForm.dataset.jsPhotosCount
    };

    // Un reglage laisse vide dans l'editeur de theme rendait « undefined » a
    // l'ecran. Mieux vaut ne rien afficher.
    Object.keys(texts).forEach(key => { texts[key] = texts[key] || ''; });

    // ── ENDPOINTS ──

    // Les URLs proviennent des réglages de section. Même garde-fou que dans
    // registration-advanced : un `javascript:` collé par erreur dans le champ ne
    // doit jamais atteindre fetch().
    function sanitizeFetchUrl(raw) {
        if (typeof raw !== 'string') return '';
        const url = raw.trim();
        return /^https?:\/\//i.test(url) ? url : '';
    }

    const uploadEndpoint = sanitizeFetchUrl(defectForm.dataset.jsUploadEndpoint);
    const webhookUrl = sanitizeFetchUrl(defectForm.dataset.jsWebhookUrl);

    if (!uploadEndpoint) console.error('[defect-form] Réglage « URL du service d\'upload » manquant ou invalide.');
    if (!webhookUrl) console.error('[defect-form] Réglage « URL du webhook » manquant ou invalide.');

    // ── IMAGE UPLOAD ──

    const MAX_IMAGE_BYTES = Number(defectForm.dataset.jsMaxImageBytes) || 10 * 1024 * 1024;
    const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/heic', 'image/heif'];

    // 15 produits × 3 photos ouvriraient 45 requêtes simultanées. Le navigateur
    // en sérialiserait la plus grande partie, mais sans plafond explicite le
    // pic mémoire et les délais d'attente deviennent ingérables sur mobile.
    const UPLOAD_CONCURRENCY = 4;
    const UPLOAD_MAX_ATTEMPTS = 3;
    const UPLOAD_RETRY_BASE_DELAY = 400;

    // Bord long maximal apres redimensionnement. 0 desactive la normalisation.
    const MAX_IMAGE_EDGE = Number(defectForm.dataset.jsMaxImageEdge) || 0;
    const IMAGE_QUALITY = 0.82;

    // Une photo déjà déposée n'est jamais renvoyée : si l'envoi échoue plus loin,
    // au webhook, la nouvelle tentative repart des URLs déjà obtenues.
    const uploadedByFile = new WeakMap();

    class PermanentUploadError extends Error {}

    const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

    /**
     * Redimensionne et re-encode avant l'envoi.
     *
     * Une photo d'iPhone pese 3 a 5 Mo ; le pire cas du formulaire, 15 produits
     * de 3 photos, represente ~180 Mo pousses depuis une 4G d'arriere-boutique.
     * Un bord long de 1600 px suffit largement au besoin metier — identifier le
     * produit et voir le defaut — pour environ un dixieme du poids.
     *
     * Toute defaillance retombe sur le fichier d'origine : Chrome et Firefox ne
     * savent pas decoder le HEIC, que le formulaire accepte pourtant. Le Worker
     * sait le stocker, donc mieux vaut envoyer l'original que rien.
     */
    async function normalizeImage(file) {
        if (!MAX_IMAGE_EDGE || typeof createImageBitmap !== 'function') return file;

        let bitmap;
        try {
            // `from-image` applique l'orientation EXIF : sans elle, les photos
            // prises en portrait arrivent couchees.
            bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
        } catch {
            return file;
        }

        try {
            const longestEdge = Math.max(bitmap.width, bitmap.height);
            const scale = Math.min(1, MAX_IMAGE_EDGE / longestEdge);

            const canvas = document.createElement('canvas');
            canvas.width = Math.round(bitmap.width * scale);
            canvas.height = Math.round(bitmap.height * scale);
            canvas.getContext('2d').drawImage(bitmap, 0, 0, canvas.width, canvas.height);

            const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', IMAGE_QUALITY));

            // Re-encoder une image deja legere peut l'alourdir : on ne garde le
            // resultat que s'il est reellement plus petit.
            if (!blob || blob.size >= file.size) return file;

            return new File([blob], file.name, { type: 'image/jpeg', lastModified: file.lastModified });
        } catch (error) {
            console.warn('[defect-form] Normalisation impossible, envoi de l\'original :', error);
            return file;
        } finally {
            bitmap.close?.();
        }
    }

    async function uploadImage(file) {
        const cached = uploadedByFile.get(file);
        if (cached) return cached;

        // Les fichiers sont deja normalises : la conversion a lieu au moment du
        // choix, pour que le controle de taille porte sur ce qui partira
        // reellement et non sur l'original.
        const payload = file;

        let lastError = new Error('upload failed');

        for (let attempt = 1; attempt <= UPLOAD_MAX_ATTEMPTS; attempt += 1) {
            try {
                const body = new FormData();
                body.append('file', payload);

                const response = await fetch(uploadEndpoint, { method: 'POST', body });

                if (response.ok) {
                    const result = await response.json();
                    if (!result || !result.url) throw new PermanentUploadError('réponse sans URL');
                    const uploaded = { name: file.name, url: result.url };
                    uploadedByFile.set(file, uploaded);
                    return uploaded;
                }

                // 4xx : refus définitif (format, taille, origine). Réessayer est inutile.
                if (response.status >= 400 && response.status < 500) {
                    const detail = await response.json().catch(() => ({}));
                    throw new PermanentUploadError(`HTTP ${response.status} ${detail.error || ''}`.trim());
                }

                lastError = new Error(`HTTP ${response.status}`);
            } catch (error) {
                if (error instanceof PermanentUploadError) throw error;
                lastError = error; // panne réseau : on retente
            }

            if (attempt < UPLOAD_MAX_ATTEMPTS) await delay(UPLOAD_RETRY_BASE_DELAY * 2 ** (attempt - 1));
        }

        throw lastError;
    }

    /** Applique `task` à tous les éléments, `limit` en vol à tout instant, ordre des résultats préservé. */
    async function mapWithConcurrency(items, limit, task) {
        const results = new Array(items.length);
        let cursor = 0;

        const workers = Array.from({ length: Math.min(limit, items.length) }, async () => {
            while (cursor < items.length) {
                const index = cursor;
                cursor += 1;
                results[index] = await task(items[index], index);
            }
        });

        await Promise.all(workers);
        return results;
    }

    // ── PRODUCT DATA ──

    // Ce JSON est produit par une boucle Liquid : un titre produit inattendu ou
    // une erreur de rendu Shopify suffirait a le rendre invalide. Sans garde, le
    // throw remontait avant l'attachement du moindre listener et le formulaire
    // entier devenait inerte, sans message.
    let productList = [];
    try {
        const parsed = JSON.parse(document.getElementById('defect-form-products')?.textContent || '[]');
        if (Array.isArray(parsed)) productList = parsed.filter(p => p && typeof p.title === 'string');
    } catch (error) {
        console.error('[defect-form] Catalogue illisible :', error);
    }

    // Le tag Liquid `paginate` n'emet que la premiere page : au-dela, des
    // produits manquent silencieusement dans le selecteur.
    const catalogCount = Number(defectForm.dataset.jsCatalogCount);
    if (Number.isFinite(catalogCount) && catalogCount > productList.length) {
        console.warn(
            `[defect-form] ${productList.length} produits rendus sur ${catalogCount} au catalogue : ` +
            'le selecteur est incomplet (limite de pagination Liquid).'
        );
    }

    // ── DOM REFERENCES ──

    const mainForm = defectForm.querySelector('.defect-form__form form');
    const addProductBtn = defectForm.querySelector('button[name="add-product"]');
    const productsList = defectForm.querySelector('.products__list');

    const newProductModal = document.querySelector('.defect-form__new-product');
    const newProductForm = newProductModal.querySelector('form');
    const modalTitle = newProductModal.querySelector('.new-product__header h2');
    const defaultModalTitle = modalTitle.textContent;

    const productNameHidden = newProductModal.querySelector('input[name="product-name"]');
    const productSkuHidden = newProductModal.querySelector('input[name="product-sku"]');
    const productImageHidden = newProductModal.querySelector('input[name="product-image"]');
    const productSelectTrigger = newProductModal.querySelector('.product-select-trigger');
    const productSelectText = productSelectTrigger.querySelector('.product-select-text');
    const productSelectImage = productSelectTrigger.querySelector('.product-select-image');
    const defaultSelectText = productSelectText.textContent;

    const serialNumberInput = newProductModal.querySelector('input[name="serial-number"]');
    const invoiceNumberInput = newProductModal.querySelector('input[name="invoice-number"]');
    const otherCheckbox = newProductModal.querySelector('.defect-other-checkbox');
    const otherInput = newProductModal.querySelector('.defect-other-input');
    const fileInput = newProductModal.querySelector('input[name="product-medias"]');
    const imageListEl = newProductModal.querySelector('.image-list');
    const infoIcon = newProductModal.querySelector('.info-icon');
    const infoTooltip = newProductModal.querySelector('.info-tooltip');

    const productSelectModal = document.querySelector('.defect-form__product-select');
    const productSearchInput = productSelectModal.querySelector('.product-search-input');
    const productDropdownList = productSelectModal.querySelector('.product-dropdown-list');

    const confirmationPopup = document.querySelector('.defect-form__confirmation');

    // ── STATE ──

    let addedProducts = [];
    let selectedFiles = [];
    let editingIndex = -1;
    // Verrouille la liste pendant l'envoi : la modifier en cours de route ferait
    // diverger ce que le client voit de ce que le service commercial recoit.
    let isSubmitting = false;

    // ── GENERIC MODAL SYSTEM ──

    const confirmModal = document.getElementById('defect-modal-confirm');
    const infoModal = document.getElementById('defect-modal-info');
    let confirmResolve = null;

    prepareModal(confirmModal, '.defect-modal__title');
    prepareModal(infoModal, '.defect-modal__title');

    function showConfirm(title, message, yesText, noText) {
        // Une confirmation qui en ecrase une autre laissait la premiere promesse
        // pendante pour toujours. On la resout par la negative avant de repartir.
        if (confirmResolve) closeConfirm(false);

        confirmModal.querySelector('.defect-modal__title').textContent = title;
        confirmModal.querySelector('.defect-modal__message').textContent = message;
        confirmModal.querySelector('.defect-modal__confirm').textContent = yesText;
        confirmModal.querySelector('.defect-modal__cancel').textContent = noText;

        return new Promise(resolve => {
            confirmResolve = resolve;
            openModal(confirmModal, { onEscape: () => closeConfirm(false) });
        });
    }

    function closeConfirm(result) {
        const resolve = confirmResolve;
        confirmResolve = null;
        closeModal(confirmModal);
        if (resolve) resolve(result);
    }

    confirmModal.querySelector('.defect-modal__confirm').addEventListener('click', () => closeConfirm(true));
    confirmModal.querySelector('.defect-modal__cancel').addEventListener('click', () => closeConfirm(false));
    confirmModal.querySelector('.defect-modal__overlay').addEventListener('click', () => closeConfirm(false));

    function showInfo(title, message) {
        infoModal.querySelector('.defect-modal__title').textContent = title;
        infoModal.querySelector('.defect-modal__message').textContent = message;
        infoModal.querySelector('.defect-modal__ok').textContent = texts.ok;
        openModal(infoModal);
    }

    function closeInfo() {
        closeModal(infoModal);
    }

    infoModal.querySelector('.defect-modal__ok').addEventListener('click', closeInfo);
    infoModal.querySelector('.defect-modal__overlay').addEventListener('click', closeInfo);

    // Shortcut helpers using texts
    function showCloseConfirm() {
        return showConfirm(texts.closeConfirmTitle, texts.closeConfirmMessage, texts.closeConfirmYes, texts.closeConfirmNo);
    }

    function showDeleteConfirm() {
        return showConfirm(texts.deleteConfirmTitle, texts.deleteConfirmMessage, texts.deleteConfirmYes, texts.deleteConfirmNo);
    }

    function showImageDeleteConfirm() {
        return showConfirm(texts.imageDeleteConfirmTitle, texts.imageDeleteConfirmMessage, texts.imageDeleteConfirmYes, texts.imageDeleteConfirmNo);
    }

    // ── MODAL ACCESSIBILITY ──

    // Les cinq boites du formulaire etaient de simples <div> montrees par un
    // attribut `open` : aucun role, aucun piege de focus, aucune touche Echap,
    // et le focus restait derriere elles. Cette couche les rend utilisables au
    // clavier sans toucher a leur rendu.

    const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), ' +
        'select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

    const modalStack = [];

    function visibleFocusable(root) {
        return [...root.querySelectorAll(FOCUSABLE)].filter(el => el.offsetParent !== null);
    }

    /**
     * Declare une boite comme dialogue et lui donne un nom accessible.
     * Ne reference aucune constante du module : les appels sont dissemines dans
     * le fichier, dont certains avant ce bloc, et une dependance a un `const`
     * declaré plus bas leverait une ReferenceError au chargement.
     */
    function prepareModal(el, titleSelector) {
        el.setAttribute('role', 'dialog');
        el.setAttribute('aria-modal', 'true');
        const title = titleSelector ? el.querySelector(titleSelector) : null;
        if (title) {
            // Identifiant derive de la modale elle-meme : chaque boite a soit un
            // id, soit une classe qui lui est propre.
            if (!title.id) title.id = `${el.id || el.className.trim().split(/\s+/)[0]}-title`;
            el.setAttribute('aria-labelledby', title.id);
        }
    }

    function openModal(el, { onEscape, initialFocus } = {}) {
        modalStack.push({ el, onEscape, restoreTo: document.activeElement });
        el.setAttribute('open', 'true');
        document.body.style.overflow = 'hidden';

        const target = (initialFocus && initialFocus.offsetParent !== null)
            ? initialFocus
            : visibleFocusable(el)[0];
        if (target) target.focus();
    }

    function closeModal(el) {
        const index = modalStack.findIndex(entry => entry.el === el);
        const entry = index >= 0 ? modalStack.splice(index, 1)[0] : null;

        el.removeAttribute('open');
        // Une modale peut en avoir ouvert une autre : on ne rend le defilement
        // qu'une fois la pile vide.
        if (modalStack.length === 0) document.body.style.overflow = '';

        const restoreTo = entry?.restoreTo;
        if (restoreTo && document.contains(restoreTo) && restoreTo.offsetParent !== null) {
            restoreTo.focus();
        }
    }

    document.addEventListener('keydown', (e) => {
        const top = modalStack[modalStack.length - 1];
        if (!top) return;

        if (e.key === 'Escape') {
            e.preventDefault();
            if (top.onEscape) top.onEscape(); else closeModal(top.el);
            return;
        }

        if (e.key !== 'Tab') return;

        // Piege de focus : sans lui, la tabulation part derriere la modale, sur
        // un formulaire toujours atteignable et pourtant masque.
        const items = visibleFocusable(top.el);
        if (items.length === 0) return;

        const first = items[0];
        const last = items[items.length - 1];
        const active = document.activeElement;

        if (e.shiftKey && (active === first || !top.el.contains(active))) {
            e.preventDefault();
            last.focus();
        } else if (!e.shiftKey && (active === last || !top.el.contains(active))) {
            e.preventDefault();
            first.focus();
        }
    });

    // ── FIELD ERRORS ──

    // Idiome repris de registration-advanced : un message persistant sous le
    // champ fautif, efface a la premiere correction. Remplace le flash de
    // bordure rouge de 2 s, souvent hors ecran sur mobile au moment du clic.
    function showFieldError(fieldEl, message) {
        if (!fieldEl) return;
        let holder = fieldEl.querySelector('.field-error');
        if (!holder) {
            holder = document.createElement('div');
            holder.className = 'field-error';
            holder.setAttribute('role', 'alert');
            fieldEl.appendChild(holder);
        }
        holder.textContent = message;
        fieldEl.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }

    function clearFieldErrors() {
        newProductForm.querySelectorAll('.field-error').forEach(el => { el.textContent = ''; });
    }

    newProductForm.addEventListener('input', (e) => {
        const holder = e.target.closest('.form__field')?.querySelector('.field-error');
        if (holder) holder.textContent = '';
    });

    newProductForm.addEventListener('change', (e) => {
        const holder = e.target.closest('.form__field')?.querySelector('.field-error');
        if (holder) holder.textContent = '';
    });

    // ── SUCCESS POPUP ──

    prepareModal(confirmationPopup, 'h3');

    function showConfirmationPopup() {
        openModal(confirmationPopup);
    }

    function closeConfirmationPopup() {
        closeModal(confirmationPopup);
    }

    confirmationPopup.querySelector('.confirmation__overlay').addEventListener('click', closeConfirmationPopup);
    confirmationPopup.querySelector('.confirmation__close-btn').addEventListener('click', closeConfirmationPopup);

    // ── PRODUCT SELECT MODAL ──

    function renderProductOptions(filter) {
        const lowerFilter = filter ? filter.toLowerCase() : '';
        const filtered = lowerFilter
            ? productList.filter(p =>
                p.title.toLowerCase().includes(lowerFilter) ||
                (p.sku && p.sku.toLowerCase().includes(lowerFilter))
            )
            : productList;

        productDropdownList.innerHTML = '';

        filtered.forEach((product, index) => {
            const div = document.createElement('div');
            div.className = 'product-option';
            div.setAttribute('role', 'option');
            div.id = `df-option-${index}`;
            const isSelected = productNameHidden.value === product.title;
            div.setAttribute('aria-selected', String(isSelected));
            if (isSelected) div.classList.add('selected');

            if (product.image) {
                const img = document.createElement('img');
                img.className = 'product-option__image';
                img.src = product.image;
                img.alt = product.title;
                img.loading = 'lazy';
                div.appendChild(img);
            }

            const name = document.createElement('span');
            name.className = 'product-option__name';
            name.textContent = product.title;
            div.appendChild(name);

            if (product.sku) {
                const sku = document.createElement('span');
                sku.className = 'product-option__sku';
                sku.textContent = product.sku;
                div.appendChild(sku);
            }

            div.addEventListener('click', () => selectProduct(product));
            productDropdownList.appendChild(div);
        });

        activeOptionIndex = -1;
        productSearchInput.removeAttribute('aria-activedescendant');
    }

    // ── PRODUCT SELECT : NAVIGATION CLAVIER ──

    let activeOptionIndex = -1;

    function optionElements() {
        return [...productDropdownList.querySelectorAll('.product-option')];
    }

    function setActiveOption(index) {
        const options = optionElements();
        if (options.length === 0) return;

        // Boucle sur les extremites : plus previsible que de buter en silence.
        activeOptionIndex = (index + options.length) % options.length;

        options.forEach((el, i) => el.classList.toggle('is-active', i === activeOptionIndex));
        const active = options[activeOptionIndex];
        active.scrollIntoView({ block: 'nearest' });
        productSearchInput.setAttribute('aria-activedescendant', active.id);
    }

    productSearchInput.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActiveOption(activeOptionIndex + 1);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveOption(activeOptionIndex - 1);
        } else if (e.key === 'Home') {
            e.preventDefault();
            setActiveOption(0);
        } else if (e.key === 'End') {
            e.preventDefault();
            setActiveOption(optionElements().length - 1);
        } else if (e.key === 'Enter') {
            // Sans ceci, Entree dans le champ de recherche soumettrait le
            // formulaire de la modale au lieu de valider le produit survole.
            e.preventDefault();
            const active = optionElements()[activeOptionIndex];
            if (active) active.click();
        }
    });

    function selectProduct(product) {
        productNameHidden.value = product.title;
        productSkuHidden.value = product.sku || '';
        productImageHidden.value = product.image || '';
        productSelectText.textContent = product.title;
        productSelectText.classList.add('has-value');

        if (product.image) {
            productSelectImage.src = product.image;
            productSelectImage.alt = product.title;
            productSelectImage.style.display = '';
        } else {
            productSelectImage.style.display = 'none';
        }

        closeProductSelect();
    }

    prepareModal(productSelectModal, '.product-select__header h3');

    function closeProductSelect() {
        closeModal(productSelectModal);
        productSelectTrigger.setAttribute('aria-expanded', 'false');
        productSearchInput.value = '';
    }

    productSelectTrigger.addEventListener('click', () => {
        renderProductOptions('');
        productSelectTrigger.setAttribute('aria-expanded', 'true');
        openModal(productSelectModal, { initialFocus: productSearchInput });
    });

    productSearchInput.addEventListener('input', () => {
        renderProductOptions(productSearchInput.value);
    });

    productSelectModal.querySelector('.product-select__close').addEventListener('click', closeProductSelect);
    productSelectModal.querySelector('.product-select__overlay').addEventListener('click', closeProductSelect);

    // ── INFO TOOLTIP ──

    if (infoIcon && infoTooltip) {
        // preventDefault empeche le <label> parent de rendre le focus au champ
        // quand on actionne le bouton d'aide qu'il contient.
        const syncTooltipState = () => {
            infoIcon.setAttribute('aria-expanded', String(infoTooltip.classList.contains('visible')));
        };

        infoIcon.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            infoTooltip.classList.toggle('visible');
            syncTooltipState();
        });

        document.addEventListener('click', (e) => {
            if (!e.target.closest('.info-icon') && !e.target.closest('.info-tooltip')) {
                infoTooltip.classList.remove('visible');
                syncTooltipState();
            }
        });

        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && infoTooltip.classList.contains('visible')) {
                infoTooltip.classList.remove('visible');
                syncTooltipState();
                infoIcon.focus();
            }
        });
    }

    // ── OTHER DEFECT TOGGLE ──

    if (otherCheckbox && otherInput) {
        otherCheckbox.addEventListener('change', () => {
            otherInput.classList.toggle('visible', otherCheckbox.checked);
            if (!otherCheckbox.checked) otherInput.value = '';
        });
    }

    // ── IMAGE LIST ──

    function renderImageList() {
        // Chaque rendu creait de nouvelles URLs d'objet sans jamais liberer les
        // precedentes : autant d'entrees retenues jusqu'au dechargement de la page.
        imageListEl.querySelectorAll('img.image-preview').forEach(img => {
            if (img.src.startsWith('blob:')) URL.revokeObjectURL(img.src);
        });

        imageListEl.innerHTML = '';

        selectedFiles.forEach((file, index) => {
            const item = document.createElement('div');
            item.className = 'image-list-item';

            const preview = document.createElement('img');
            preview.className = 'image-preview';
            preview.alt = file.name;
            if (file instanceof File) preview.src = URL.createObjectURL(file);

            const name = document.createElement('span');
            name.className = 'image-name';
            name.textContent = file.name;

            const removeBtn = document.createElement('button');
            removeBtn.type = 'button';
            removeBtn.className = 'image-remove';
            removeBtn.innerHTML = '&times;';
            removeBtn.setAttribute('aria-label', `${texts.remove} — ${file.name}`);
            removeBtn.addEventListener('click', async () => {
                if (await showImageDeleteConfirm()) {
                    selectedFiles.splice(index, 1);
                    renderImageList();
                    updateFileInputBtn();
                }
            });

            item.append(preview, name, removeBtn);
            imageListEl.appendChild(item);
        });
    }

    const MAX_IMAGES = 3;
    const fileInputBtn = document.getElementById('product-media-btn');

    function updateFileInputBtn() {
        if (fileInputBtn) {
            fileInputBtn.disabled = selectedFiles.length >= MAX_IMAGES;
            fileInputBtn.style.opacity = selectedFiles.length >= MAX_IMAGES ? '0.5' : '1';
        }
    }

    /**
     * Controle au moment du choix plutot qu'a l'envoi : le client corrige tant
     * qu'il a la photo sous les yeux, au lieu d'essuyer un rejet du serveur
     * apres avoir saisi quinze produits.
     *
     * La normalisation a lieu ici, avant le controle de taille : le plafond doit
     * porter sur ce qui partira reellement. Une photo de 30 Mo redimensionnee a
     * 300 Ko doit etre acceptee ; la meme en HEIC sur un navigateur qui ne sait
     * pas la decoder doit etre refusee, tout de suite et avec un motif.
     */
    if (fileInput) {
        fileInput.addEventListener('change', async () => {
            const chosen = Array.from(fileInput.files);
            fileInput.value = '';

            const rejections = new Set();
            const wasDisabled = fileInputBtn?.disabled;
            if (fileInputBtn) fileInputBtn.disabled = true;

            try {
                for (const original of chosen) {
                    // Le plafond etait applique en silence : les fichiers au-dela
                    // du troisieme disparaissaient sans un mot.
                    if (selectedFiles.length >= MAX_IMAGES) {
                        rejections.add(texts.maxImagesReached);
                        continue;
                    }

                    // Plusieurs navigateurs ne renseignent pas le type MIME des
                    // HEIC : on ne refuse que sur un type explicitement hors
                    // liste, jamais sur un vide.
                    if (original.type && !ACCEPTED_IMAGE_TYPES.includes(original.type)) {
                        rejections.add(texts.imageWrongType);
                        continue;
                    }

                    const file = await normalizeImage(original);

                    if (file.size > MAX_IMAGE_BYTES) {
                        rejections.add(texts.imageTooLarge);
                        continue;
                    }

                    if (!selectedFiles.some(e => e.name === file.name && e.size === file.size)) {
                        selectedFiles.push(file);
                    }
                }
            } finally {
                if (fileInputBtn) fileInputBtn.disabled = wasDisabled;
            }

            renderImageList();
            updateFileInputBtn();

            if (rejections.size) showInfo(texts.imageRejectedTitle, [...rejections].join(' '));
        });
    }

    fileInputBtn?.addEventListener('click', () => {
        if (selectedFiles.length >= MAX_IMAGES) return;
        fileInput?.click();
    });

    // ── INPUT FORMATTERS ──

    // La periode acceptee est recalculee a chaque rendu a partir de l'annee de la
    // boutique. L'ancienne liste ['2024','2025','2026'] etait figee dans le code
    // alors que le Liquid pre-remplit « F-<annee courante>- » : au 1er janvier
    // suivant, plus aucune facture de l'annee en cours n'etait declarable.
    const currentYear = Number(defectForm.dataset.jsCurrentYear) || new Date().getFullYear();
    const yearsBack = Number(defectForm.dataset.jsInvoiceYearsBack);
    const INVOICE_MAX_YEAR = currentYear;
    const INVOICE_MIN_YEAR = currentYear - (Number.isFinite(yearsBack) ? yearsBack : 2);

    function invoiceYearMessage() {
        return texts.invoiceYearError
            .replace('{min}', INVOICE_MIN_YEAR)
            .replace('{max}', INVOICE_MAX_YEAR);
    }

    if (serialNumberInput) {
        serialNumberInput.addEventListener('input', () => {
            serialNumberInput.value = serialNumberInput.value.replace(/[^0-9]/g, '').slice(0, 4);
        });
    }

    if (invoiceNumberInput) {
        invoiceNumberInput.addEventListener('input', () => {
            let val = invoiceNumberInput.value;
            if (!val.startsWith('F-')) val = 'F-';

            const digits = val.slice(2).replace(/[^0-9]/g, '');
            val = digits.length <= 4
                ? 'F-' + digits
                : 'F-' + digits.slice(0, 4) + '-' + digits.slice(4, 8);

            invoiceNumberInput.value = val;

            if (digits.length >= 4) {
                const year = Number(digits.slice(0, 4));
                const inWindow = year >= INVOICE_MIN_YEAR && year <= INVOICE_MAX_YEAR;
                invoiceNumberInput.setCustomValidity(inWindow ? '' : invoiceYearMessage());
            } else {
                invoiceNumberInput.setCustomValidity('');
            }
        });

        invoiceNumberInput.addEventListener('focus', () => {
            if (!invoiceNumberInput.value) invoiceNumberInput.value = 'F-';
        });
    }

    // ── NEW PRODUCT MODAL ──

    prepareModal(newProductModal, '.new-product__header h2');

    function openNewProductModal() {
        openModal(newProductModal, {
            onEscape: handleModalClose,
            initialFocus: productSelectTrigger
        });
    }

    function closeNewProductModal() {
        closeModal(newProductModal);
    }

    function resetModalForm() {
        newProductForm.reset();
        productNameHidden.value = '';
        productSkuHidden.value = '';
        productImageHidden.value = '';
        productSelectText.textContent = defaultSelectText;
        productSelectText.classList.remove('has-value');
        productSelectImage.style.display = 'none';
        productSelectImage.src = '';
        productSearchInput.value = '';
        selectedFiles = [];
        renderImageList();
        if (otherInput) {
            otherInput.classList.remove('visible');
            otherInput.value = '';
        }
        if (invoiceNumberInput) {
            invoiceNumberInput.value = 'F-';
            // Une affectation par programme n'emet pas d'evenement `input` : sans
            // remise a zero explicite, une saisie hors periode sur un produit
            // bloquait la validation du produit suivant.
            invoiceNumberInput.setCustomValidity('');
        }
        clearFieldErrors();
        editingIndex = -1;
        updateFileInputBtn();
    }

    function prefillModalForEdit(data) {
        productNameHidden.value = data.name;
        productSkuHidden.value = data.sku || '';
        productImageHidden.value = data.image || '';
        productSelectText.textContent = data.name;
        productSelectText.classList.add('has-value');

        if (data.image) {
            productSelectImage.src = data.image;
            productSelectImage.alt = data.name;
            productSelectImage.style.display = '';
        } else {
            productSelectImage.style.display = 'none';
        }

        if (serialNumberInput) serialNumberInput.value = data.serialNumber || '';
        if (invoiceNumberInput) {
            invoiceNumberInput.value = data.invoiceNumber || 'F-';
            invoiceNumberInput.setCustomValidity('');
        }

        newProductForm.querySelectorAll('input[name="defect-types[]"]').forEach(cb => {
            const hasOther = data.defectTypes.some(d => d.startsWith('Other: '));
            if (cb.value === 'other' && hasOther) {
                cb.checked = true;
                if (otherInput) {
                    const otherVal = data.defectTypes.find(d => d.startsWith('Other: '));
                    otherInput.value = otherVal ? otherVal.replace('Other: ', '') : '';
                    otherInput.classList.add('visible');
                }
            } else {
                cb.checked = data.defectTypes.includes(cb.value);
            }
        });

        selectedFiles = [...(data.mediaFiles || [])];
        renderImageList();
        // Sans cet appel, rouvrir un produit deja pourvu de 3 photos affichait un
        // bouton « Choisir des fichiers » actif mais sans effet.
        updateFileInputBtn();
    }

    // Open modal for adding
    addProductBtn.addEventListener('click', () => {
        if (isSubmitting) return;
        if (addedProducts.length >= 15) {
            showInfo(texts.limitReached, texts.cannotAddMore);
            return;
        }
        editingIndex = -1;
        modalTitle.textContent = defaultModalTitle;
        openNewProductModal();
    });

    // Close modal handlers
    async function handleModalClose() {
        if (await showCloseConfirm()) {
            closeNewProductModal();
            resetModalForm();
        }
    }

    newProductModal.querySelector('button[name="cancel"]').addEventListener('click', handleModalClose);
    newProductModal.querySelector('.new-product__overlay').addEventListener('click', handleModalClose);

    // ── PRODUCT CARDS ──

    function renderProductCards() {
        productsList.innerHTML = '';

        addedProducts.forEach((data, index) => {
            const card = document.createElement('div');
            card.className = 'product';

            const fileCount = (data.mediaFiles || []).length;

            // Construction par createElement plutot que par template literal : le
            // texte libre du champ « Autre » et les titres produit sont des
            // donnees, pas du balisage. Un simple « < » tronquait la carte.
            const infos = document.createElement('div');
            infos.className = 'product-informations';

            const nameEl = document.createElement('div');
            nameEl.className = 'product-name';
            nameEl.textContent = data.name;

            const details = document.createElement('div');
            details.className = 'product-details';
            [
                data.serialNumber,
                data.invoiceNumber,
                texts.photosCount.replace('{n}', fileCount)
            ].forEach(value => {
                const span = document.createElement('span');
                span.textContent = value;
                details.appendChild(span);
            });

            const defectsEl = document.createElement('div');
            defectsEl.className = 'product-defects';
            defectsEl.textContent = (data.defectLabels || data.defectTypes).join(', ');

            infos.append(nameEl, details, defectsEl);

            const actions = document.createElement('div');
            actions.className = 'product-actions';

            const editBtn = document.createElement('button');
            editBtn.type = 'button';
            editBtn.className = 'product-edit-btn';
            editBtn.textContent = texts.edit;
            editBtn.setAttribute('aria-label', `${texts.edit} — ${data.name}`);

            const removeBtn = document.createElement('button');
            removeBtn.type = 'button';
            removeBtn.className = 'product-remove-btn';
            removeBtn.textContent = texts.remove;
            removeBtn.setAttribute('aria-label', `${texts.remove} — ${data.name}`);

            actions.append(editBtn, removeBtn);
            card.append(infos, actions);

            card.querySelector('.product-edit-btn').addEventListener('click', () => {
                if (isSubmitting) return;
                editingIndex = index;
                modalTitle.textContent = texts.editProductTitle;
                resetModalForm();
                editingIndex = index;
                prefillModalForEdit(data);
                openNewProductModal();
            });

            card.querySelector('.product-remove-btn').addEventListener('click', async () => {
                if (isSubmitting) return;
                if (await showDeleteConfirm()) {
                    // L'envoi a pu demarrer pendant que la confirmation etait ouverte.
                    if (isSubmitting) return;
                    addedProducts.splice(index, 1);
                    renderProductCards();
                    updateAddProductButton();
                }
            });

            productsList.appendChild(card);
        });
    }

    /**
     * Neutralise les actions qui muteraient la liste pendant l'envoi. Sans ce
     * verrou, retirer un produit en cours de transfert affiche 14 cartes au
     * client pendant que le service commercial en recoit 15.
     */
    function setListLocked(locked) {
        productsList.classList.toggle('is-locked', locked);
        productsList.querySelectorAll('button').forEach(btn => { btn.disabled = locked; });
        if (locked) {
            addProductBtn.disabled = true;
        } else {
            updateAddProductButton();
        }
    }

    function updateAddProductButton() {
        if (addedProducts.length >= 15) {
            addProductBtn.disabled = true;
            addProductBtn.textContent = texts.maximumReached;
            addProductBtn.style.opacity = '0.5';
        } else {
            addProductBtn.disabled = false;
            addProductBtn.textContent = addedProducts.length > 0
                ? `${texts.addProductText} (${addedProducts.length}/15)`
                : texts.addProductText;
            addProductBtn.style.opacity = '1';
        }
    }

    // ── PRODUCT FORM SUBMIT ──

    newProductForm.addEventListener('submit', (e) => {
        e.preventDefault();

        if (editingIndex === -1 && addedProducts.length >= 15) {
            showInfo(texts.limitReached, texts.cannotAddMore);
            closeNewProductModal();
            return;
        }

        // Un seul idiome pour les trois validations : message persistant sous le
        // champ, defilement jusqu'a lui. Auparavant chacune avait le sien, dont
        // une ouverture silencieuse du selecteur de fichiers sans issue.
        clearFieldErrors();

        if (!productNameHidden.value) {
            showFieldError(productSelectTrigger.closest('.form__field'), texts.noProductSelected);
            return;
        }

        if (!newProductForm.querySelectorAll('input[name="defect-types[]"]:checked').length) {
            showFieldError(newProductForm.querySelector('.defect-checkboxes').closest('.form__field'), texts.minOneDefect);
            return;
        }

        if (selectedFiles.length === 0) {
            showFieldError(fileInput.closest('.form__field'), texts.minOneImage);
            return;
        }

        // Codes ET libelles traduits. Les codes restent la sortie machine attendue
        // par Make ; les libelles permettent au revendeur de relire sa
        // declaration autrement qu'en « crack, spare_part ».
        const checkedBoxes = [...newProductForm.querySelectorAll('input[name="defect-types[]"]:checked')];
        const checkedDefects = checkedBoxes.map(cb => cb.value);
        const defectLabels = checkedBoxes.map(cb => cb.dataset.label || cb.value);

        const defectOtherText = newProductForm.querySelector('input[name="defect-other-text"]')?.value || '';
        if (defectOtherText && checkedDefects.includes('other')) {
            const otherIndex = checkedDefects.indexOf('other');
            checkedDefects[otherIndex] = 'Other: ' + defectOtherText; // contrat Make inchange
            defectLabels[otherIndex] = defectOtherText;
        }

        const invoiceNumber = invoiceNumberInput.value;
        const invoiceMatch = invoiceNumber.match(/^F-(\d{4})-\d{4}$/);
        const mediaFiles = [...selectedFiles];

        const productData = {
            name: productNameHidden.value,
            sku: productSkuHidden.value,
            image: productImageHidden.value || '',
            serialNumber: serialNumberInput.value,
            invoiceNumber: invoiceNumber,
            invoiceYear: invoiceMatch ? invoiceMatch[1] : '',
            defectTypes: checkedDefects,
            defectLabels: defectLabels,
            defectOther: defectOtherText,
            mediaFiles: mediaFiles,
            mediaFileNames: mediaFiles.map(f => f.name),
            mediaFileTypes: mediaFiles.map(f => f.type),
            mediaFileSizes: mediaFiles.map(f => f.size)
        };

        if (editingIndex >= 0) {
            addedProducts[editingIndex] = productData;
        } else {
            addedProducts.push(productData);
        }

        renderProductCards();
        updateAddProductButton();
        closeNewProductModal();
        resetModalForm();
    });

    // ── HELPERS ──

    // ── MAIN FORM SUBMIT ──

    mainForm.addEventListener('submit', async (e) => {
        e.preventDefault();

        if (isSubmitting) return;

        if (addedProducts.length === 0) {
            showInfo(texts.minOneProduct, texts.minOneProduct);
            return;
        }

        // Mal configurée, la section enverrait les photos dans le vide et le
        // client croirait sa demande partie. Mieux vaut refuser tout de suite.
        if (!uploadEndpoint || !webhookUrl) {
            console.error('[defect-form] Envoi annulé : endpoints non configurés dans les réglages de la section.');
            showInfo(texts.errorMessage, texts.errorMessage);
            return;
        }

        const formData = new FormData(mainForm);
        const submitBtn = mainForm.querySelector('button[type="submit"]');
        const originalText = submitBtn.textContent;

        try {
            isSubmitting = true;
            setListLocked(true);
            submitBtn.textContent = texts.sendingText;
            submitBtn.disabled = true;

            // Instantane fige : tout ce qui suit s'indexe sur `products` et jamais
            // sur `addedProducts`, qui est relu apres des `await`. Sans cela, une
            // suppression concurrente decalerait les indices et rattacherait des
            // photos au mauvais produit.
            const products = addedProducts.slice();

            // Toutes les photos de tous les produits sont mises à plat : la limite
            // de concurrence s'applique ainsi globalement, et la progression
            // affichée reflète le total réel et non un produit à la fois.
            const uploadQueue = [];
            products.forEach((p, pIndex) => {
                (p.mediaFiles || []).forEach((file, fIndex) => uploadQueue.push({ pIndex, fIndex, file }));
            });

            let uploadedCount = 0;
            const uploads = await mapWithConcurrency(uploadQueue, UPLOAD_CONCURRENCY, async (item) => {
                const { name, url } = await uploadImage(item.file);
                uploadedCount += 1;
                submitBtn.textContent = `${texts.sendingText} (${uploadedCount}/${uploadQueue.length})`;
                return { ...item, name, url };
            });

            // Reconstruit `image_N_name` / `image_N_url` par produit, dans l'ordre
            // de saisie : la forme du payload attendue par le scénario Make est
            // strictement conservée.
            const imagesByProduct = products.map(() => ({}));
            uploads.forEach(({ pIndex, fIndex, name, url }) => {
                imagesByProduct[pIndex][`image_${fIndex + 1}_name`] = name;
                imagesByProduct[pIndex][`image_${fIndex + 1}_url`] = url;
            });

            const productsPayload = products.map((p, pIndex) => ({
                name: p.name,
                sku: p.sku || '',
                serialNumber: p.serialNumber,
                invoiceNumber: p.invoiceNumber,
                invoiceYear: p.invoiceYear || '',
                defectTypes: p.defectTypes.join(', '),
                // Ajouts additifs : les cles existantes sont inchangees, le
                // scenario Make continue de fonctionner sans modification.
                defectLabels: (p.defectLabels || p.defectTypes).join(', '),
                defectOther: p.defectOther || '',
                images: imagesByProduct[pIndex]
            }));

            const submissionData = {
                language: formData.get('language'),
                firstName: formData.get('first-name'),
                lastName: formData.get('last-name'),
                email: formData.get('email'),
                company: formData.get('company'),
                returnDate: formData.get('return-date'),
                otherInfo: formData.get('other-info') || '',
                submissionDate: new Date().toISOString(),
                products: productsPayload
            };

            const response = await fetch(webhookUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(submissionData)
            });

            if (response.ok) {
                showConfirmationPopup();
                mainForm.reset();
                addedProducts = [];
                productsList.innerHTML = '';
                updateAddProductButton();
            } else {
                const errorText = await response.text();
                console.error('Server error:', errorText);
                throw new Error(`HTTP ${response.status}: ${errorText}`);
            }
        } catch (error) {
            console.error('Send error:', error);
            showInfo(texts.errorMessage, texts.errorMessage);
        } finally {
            isSubmitting = false;
            setListLocked(false);
            submitBtn.textContent = originalText;
            submitBtn.disabled = false;
        }
    });

}
